import supabase from "./supabase.js";

const getEventValue = (event, ...keys) =>
    keys.reduce((value, key) => value ?? event?.[key], undefined);

const needsResearchEnrichment = (event) =>
    !String(
        getEventValue(
            event,
            "ticketsUrl",
            "TicketsUrl",
            "ticketUrl",
            "TicketUrl",
            "eventUrl",
            "EventUrl"
        ) || ""
    ).trim() ||
    !String(getEventValue(event, "description", "Description") || "").trim();

const getEventIdentity = (event) =>
    String(getEventValue(event, "name", "Name") || "")
        .trim()
        .toLocaleLowerCase();

const mergeCitations = (...citationLists) => {
    const seen = new Set();

    return citationLists.flat().filter((citation) => {
        const citationData = citation?.url_citation || citation;
        const key = citationData?.url || JSON.stringify(citation);

        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
    });
};

const invokeEventSearch = async (body) => {
    const { data, error } = await supabase.functions.invoke("ai-fetch-events", {
        body,
    });

    if (error) {
        console.error("[Edge Function Error]", error);
        throw new Error(
            error.message ||
            "Failed to fetch events from the edge function"
        );
    }

    if (data?.error) {
        console.error("[Edge Function Response Error]", data);
        throw new Error(
            data.error ||
            "Edge function returned an error"
        );
    }

    return data || {};
};

/**
 * Searches for queer events using the ai-fetch-events edge function.
 *
 * @param {Object} params
 * @param {string} params.country - Country name (e.g., "Australia")
 * @param {string} [params.countryCode] - Country code (e.g., "AU")
 * @param {string} params.state - State or region name
 * @param {string} params.city - City name
 * @param {string} [params.prompt] - Custom search prompt
 * @returns {Promise<{events: Array, citations: Array, raw: string}>}
 */
export async function searchEventsWithAI({ country, countryCode, state, city, prompt }) {
    if (!country?.trim()) {
        throw new Error("Country is required");
    }

    if (!city?.trim()) {
        throw new Error("City is required");
    }

    const requestLocation = {
        country: country.trim(),
        countryCode: countryCode?.trim(),
        state: state?.trim(),
        city: city.trim(),
    };
    const data = await invokeEventSearch({
        ...requestLocation,
        prompt: prompt?.trim(),
    });
    const events = data.events || [];
    const incompleteEvents = events.filter(needsResearchEnrichment);

    if (incompleteEvents.length === 0) {
        return {
            events,
            citations: data.citations || [],
            raw: data.raw || "",
        };
    }

    const researchTargets = incompleteEvents.map((event) => ({
        name: getEventValue(event, "name", "Name") || "",
        venue: getEventValue(event, "venue", "Venue") || "",
        startDatetime:
            getEventValue(event, "startDatetime", "StartDateTime") || "",
    }));

    try {
        const enrichment = await invokeEventSearch({
            ...requestLocation,
            prompt: `Research only the following already-discovered events:\n${JSON.stringify(researchTargets)}\n\nReturn exactly one JSON object for each listed event, in the same order. Each object must include name, ticketsUrl, and description. ticketsUrl must be a real, direct, verified official event or ticket page—not a homepage, search result, social profile, or invented URL. Read that page and write an original 1–3 sentence description that is fun, cheeky, welcoming, useful, and factual. Paraphrase rather than copying promotional text. Return plain description text without citations, URLs, or Markdown. Exclude unsupported claims. Return only a JSON array.`,
        });
        const enrichedByName = new Map(
            (enrichment.events || []).map((event) => [
                getEventIdentity(event),
                event,
            ])
        );
        let enrichmentIndex = 0;
        const mergedEvents = events.map((event) => {
            if (!needsResearchEnrichment(event)) return event;

            const enrichedEvent =
                enrichedByName.get(getEventIdentity(event)) ||
                enrichment.events?.[enrichmentIndex];
            enrichmentIndex += 1;

            if (!enrichedEvent) return event;

            return {
                ...event,
                ticketsUrl:
                    getEventValue(
                        event,
                        "ticketsUrl",
                        "TicketsUrl",
                        "ticketUrl",
                        "TicketUrl",
                        "eventUrl",
                        "EventUrl"
                    ) ||
                    getEventValue(
                        enrichedEvent,
                        "ticketsUrl",
                        "TicketsUrl",
                        "ticketUrl",
                        "TicketUrl",
                        "eventUrl",
                        "EventUrl"
                    ) ||
                    "",
                description:
                    getEventValue(event, "description", "Description") ||
                    getEventValue(
                        enrichedEvent,
                        "description",
                        "Description"
                    ) ||
                    "",
            };
        });

        return {
            events: mergedEvents,
            citations: mergeCitations(
                data.citations || [],
                enrichment.citations || []
            ),
            raw: data.raw || "",
        };
    } catch (error) {
        console.warn("[Event Enrichment Warning]", error);
    }

    return {
        events,
        citations: data.citations || [],
        raw: data.raw || "",
    };
}
